"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Notification/NotificationSignalHeader";

import { cn } from "utils";
import { useCallback } from "react";
import { AlphaPickPricingModalCallable } from "../AlphaPick/AlphaPickPricing";
import {
  NotificationSignalHeaderLabel,
  type NotificationSignalHeaderLabelItem,
} from "./NotificationSignalHeaderLabel";
import { useIsPc } from "@/src/hooks";
import NextImage from "../NextImage";
import log from "logbeacon";

const I18n = I18nUtil.createScoped(Translations);

/** Non-member CTA — Figma 81033-18524 */
const activateEdButtonClass = cn(
  "inline-flex w-fit items-center justify-center gap-1 rounded-[6px] border-0 px-2 py-1.5",
  "bg-background_brand_overlay100 text-content_inverse_primary",
);

export interface NotificationSignalHeaderProps {
  needActive: boolean;
  labelItems: NotificationSignalHeaderLabelItem[];
  totalSignals: number;
}

/**
 * Holdings signals header on Notifications (Figma 81033-18524 credit exhausted).
 * Shown only when parent resolves needActive; copy matches that state.
 */
export function NotificationSignalHeader({
  needActive,
  labelItems,
  totalSignals,
}: NotificationSignalHeaderProps) {
  const [isPC] = useIsPc();
  const title = `${totalSignals} ` + I18n.t("New Signals Found by Ed");
  const desc = I18n.t("Uh oh! You've reached your limit.");

  const handleActiveEdClick = useCallback(async () => {
    log.trace("[GTM_CLICK][bell_activate_ed_tap]");
    await AlphaPickPricingModalCallable({
      isPC,
      trackingModuleName: "edgen_notification_active",
      paywallCopyOverrides: {
        title: I18n.t("Someone should always be watching the market for you"),
        subtitle: I18n.t("Ed already is."),
        features: [
          I18n.t("Track your holdings while you're away"),
          I18n.t("Flag risks before they hit"),
          I18n.t("Full read on all stocks"),
        ],
        hideSocialProof: true,
      },
    });
  }, [isPC]);

  return (
    <div
      className={cn(
        "pl-2.5 pr-4 flex py-3 border-b border-border_white_transparent5 gap-2",
        {
          " bg-background_brand_overlay5": needActive,
        },
      )}
    >
      <NextImage
        src="/assets/images/unread_dot.svg"
        className={cn(
          "w-1.5 h-1.5 mt-[31px]",
          needActive ? "visible" : "invisible",
        )}
        width={6}
        height={6}
      />
      <div className="flex flex-col">
        <div className="flex flex-col gap-0.5">
          <span className="body_bold_m text-content_primary">{title}</span>
          <span className="body_m text-content_secondary">{desc}</span>
        </div>
        <NotificationSignalHeaderLabel items={labelItems} className="mt-2" />
        {needActive ? (
          <button
            type="button"
            data-openreplay-label="Notification signal header Activate Ed"
            className={cn("mt-4", activateEdButtonClass)}
            onClick={handleActiveEdClick}
          >
            <span className="button_s">{I18n.t("Recharge Ed")}</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}
